import { Trans, useLingui } from "@lingui/react/macro";
import type { ApprovalPolicy, ApprovalPolicyRuleItem } from "@rakazo/contracts";
import { humanizeToolName } from "@rakazo/core";
import { writeSaved } from "@rakazo/core/query";
import { Badge, Button, Input } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";
import { SettingsGroup, SettingsRow } from "./SettingsRows";

/**
 * Approval rules typed in plain English, for the whole Space or one bot. The server reads each
 * rule into what it enforces; the chips show that reading before and after saving.
 */
export function PlainRulesSettings({ botId, title }: { botId?: string; title: ReactNode }) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const input = botId ? { botId } : {};
  const list = useQuery(queries.approvalPolicies.list.queryOptions({ input }));
  const [text, setText] = useState("");
  const typed = useSettled(text.trim(), 400);
  const preview = useQuery({
    ...queries.approvalPolicies.preview.queryOptions({ input: { text: typed } }),
    enabled: Boolean(list.data?.canEdit) && typed.length > 2,
    retry: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const policies = list.data?.policies ?? [];
  const canEdit = Boolean(list.data?.canEdit);

  function setPolicies(update: (current: ApprovalPolicy[]) => ApprovalPolicy[]) {
    return writeSaved(queryClient, queries.approvalPolicies.list.queryKey({ input }), (current) =>
      current
        ? { ...current, policies: update(current.policies) }
        : { policies: update([]), canEdit: true, alwaysAsk: [] },
    );
  }

  async function save(work: () => Promise<unknown>, failure: string) {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(errorText(err, failure));
    } finally {
      setSaving(false);
    }
  }

  const add = () =>
    save(async () => {
      const saved = await rpc.approvalPolicies.add({ ...input, text: text.trim() });
      await setPolicies((current) => [...current, saved]);
      setText("");
    }, t`Could not save rule`);

  const remove = (policy: ApprovalPolicy) =>
    save(async () => {
      await rpc.approvalPolicies.remove({ id: policy.id });
      await setPolicies((current) => current.filter((other) => other.id !== policy.id));
    }, t`Could not remove rule`);

  if (!list.data || (!canEdit && policies.length === 0)) return null;
  const previewError =
    preview.error && typed === text.trim()
      ? errorText(preview.error, t`Could not read rule`)
      : null;
  return (
    <SettingsGroup title={title} data-testid={botId ? "bot-plain-rules" : "space-plain-rules"}>
      {policies.map((policy) => (
        <SettingsRow
          key={policy.id}
          label={policy.text}
          control={
            canEdit ? (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t`Remove`}
                className="text-destructive hover:text-destructive"
                disabled={saving}
                onClick={() => void remove(policy)}
              >
                <Trash2 />
              </Button>
            ) : undefined
          }
        >
          <RuleChips rules={policy.rules} />
        </SettingsRow>
      ))}
      {canEdit ? (
        <SettingsRow
          label={
            <form
              className="flex w-full min-w-64 items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (text.trim()) void add();
              }}
            >
              <Input
                aria-label={t`New rule`}
                placeholder={t`e.g. Ask before purchases`}
                value={text}
                maxLength={200}
                onChange={(event) => setText(event.target.value)}
              />
              <Button type="submit" size="sm" disabled={saving || !text.trim()}>
                <Trans>Add</Trans>
              </Button>
            </form>
          }
        >
          {text.trim() && preview.data && typed === text.trim() ? (
            <RuleChips rules={preview.data.rules} />
          ) : null}
          {previewError ? (
            <p className="mt-1 text-[13px] text-muted-foreground">{previewError}</p>
          ) : null}
        </SettingsRow>
      ) : null}
      {list.data.alwaysAsk.length > 0 ? (
        <SettingsRow label={<Trans>Always asks</Trans>}>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {list.data.alwaysAsk.map(humanizeToolName).join(", ")}
          </p>
        </SettingsRow>
      ) : null}
      {error ? <p className="px-4 py-2 text-[13px] text-destructive">{error}</p> : null}
    </SettingsGroup>
  );
}

function RuleChips({ rules }: { rules: ApprovalPolicyRuleItem[] }) {
  const { t } = useLingui();
  const effect = {
    require_approval: t`Ask`,
    always_allow: t`Allow`,
    deny: t`Block`,
  } as const;
  const category: Record<string, string> = {
    purchase: t`Purchases`,
    email: t`Email`,
    email_send: t`Sending email`,
    email_draft: t`Email drafts`,
    message_send: t`Sending messages`,
    delete: t`Deleting`,
  };
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="rule-chips">
      {rules.map((rule) => (
        <Badge key={`${rule.matchKind}:${rule.matchValue}`} variant="secondary">
          {effect[rule.effect]} ·{" "}
          {rule.matchKind === "category"
            ? (category[rule.matchValue] ?? rule.matchValue)
            : rule.matchKind === "site"
              ? rule.matchValue
              : humanizeToolName(rule.matchValue)}
        </Badge>
      ))}
    </div>
  );
}

/** The value once it has stopped changing for `ms`, so typing does not ask the server per key. */
function useSettled(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}
